import { Component, EventEmitter, Input, OnInit, OnChanges, SimpleChanges, Output, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor, FormControl, FormGroup } from '@angular/forms';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { NgIf, NgFor, UpperCasePipe } from '@angular/common';
import { CacheService } from '../../../services/cache.service';
import { CACHE_KEYS } from '../../../constants/cache-keys';
import { LoginResults } from '../../../../login/login.viewmodel';
import { MatIconModule } from "@angular/material/icon";

@Component({
  selector: 'nuc-select',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, MatSelectModule, NgIf, NgFor, MatIconModule],
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SelectComponent,
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.None,
})
export class SelectComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() options: any[] = [];
  @Input() displayKey: string = 'value';
  @Input() label: string = '';
  @Input() formControl: FormControl | null = null;
  @Input() errorMessage: string = 'Invalid selection';
  @Input() displayClass: string = '';
  @Input() displayIconClass: string = '';
  @Input() isDisabled: boolean = false;
  @Input() trackByKey: string;
  @Input() iconClass: string;
  @Input() size: string;
  @Output() selectionChange = new EventEmitter<any>();
  @Input() panelClass: string = '';
  userType: string;
  loginData?: LoginResults;
  value: any = null;
  optionType: 'primitive' | 'object' = 'primitive';

  constructor(private cacheService: CacheService){
  }

  ngOnInit(): void {
    this.loginData = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LOGIN_DATA);
    this.userType = this.loginData?.userType;
    this.setOptionType();
    if (this.formControl && this.isDisabled) {
      this.formControl.disable();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.loginData = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LOGIN_DATA);
    this.userType = this.loginData?.userType;
    if (changes['options']) {
      this.setOptionType();
    }
    if (changes['isDisabled'] && this.formControl) {
      changes['isDisabled'].currentValue ? this.formControl.disable() : this.formControl.enable();
    }
  }

  private setOptionType() {
    if (this.options && this.options.length > 0) {
      this.optionType = typeof this.options[0] === 'object' ? 'object' : 'primitive';
    } else {
      this.optionType = 'primitive';
    }
  }

  getControlName(control: FormControl): string | null {
    if (control?.parent instanceof FormGroup) {
      const formGroup = control.parent;
      for (const controlName in formGroup.controls) {
        if (formGroup.controls[controlName] === control) return controlName;
      }
    }
    return null;
  }

  onSelectionChange(event: MatSelectChange) {
    this.selectionChange.emit(event.value);
    // If you want this component to act as a full CVA for template-driven usage:
    this.onChange(event.value);
    this.onTouched();
  }

  // Optional: proper compare for object values
  compareWith = (a: any, b: any) => {
    if (!a || !b) return a === b;
    // Prefer trackByKey if provided, else fall back to common keys
    if (this.trackByKey && a[this.trackByKey] && b[this.trackByKey]) {
      return a[this.trackByKey] === b[this.trackByKey];
    }
    if ('isoCode' in a && 'isoCode' in b) return a.isoCode === b.isoCode;
    if ('languageCode' in a && 'languageCode' in b) return a.languageCode === b.languageCode;
    return JSON.stringify(a) === JSON.stringify(b);
  };

  // CVA plumbing
  onChange = (_: any) => {};
  onTouched = () => {};

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  trackByFunction(index: number, option: any): any {
    return this.trackByKey ? option[this.trackByKey] : index;
  }
}
